import { useEffect, useState, useCallback } from "react";
import { ChevronDown, ArrowLeft, Trophy, Target, Goal } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import api from "../../utils/axiosConfig";

// Cache buster - force component reload with unique identifier
const COMPONENT_VERSION = "v2.1.0"; // Update this when you want to force refresh

export default function IndividualMatchDetails() {
  const [selectedTeam, setSelectedTeam] = useState("Las Palmas");
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [activeTab, setActiveTab] = useState("predictions");
  const [selectedLineupTeam, setSelectedLineupTeam] = useState('home');
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get match value from URL query parameters
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('match');

  // Force cache refresh on component mount
  useEffect(() => {
    // Log component version for debugging
    console.log(`IndividualMatchDetails ${COMPONENT_VERSION} loaded`);
    
    // Clear any potential cached data
    if ('caches' in window) {
      caches.keys().then(function(names) {
        names.forEach(function(name) {
          if (name.includes('static') || name.includes('js') || name.includes('css')) {
            console.log('Clearing cache:', name);
            caches.delete(name);
          }
        });
      });
    }
  }, []);

  // Mock data for demonstration
  useEffect(() => {
    const fetchMatchData = async () => {
      try {
        // Add cache busting to API calls
        const response = await api.get(`/fixtures/fixture/${matchId}?v=${COMPONENT_VERSION}&t=${Date.now()}`);
        setMatchData(response.data.fixture);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching match data:", error);
        setLoading(false);
      }
    };
    fetchMatchData();
  }, [matchId]);

  // Player image component with fallback
  const PlayerImage = ({ player, size = "w-10 h-10", showStats = false, statsValue = null, teamColors = null }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleImageLoad = () => {
      setImageLoaded(true);
      setImageError(false);
    };

    const handleImageError = () => {
      setImageLoaded(false);
      setImageError(true);
    };

    return (
      <div className="relative">
        {player.photo && !imageError && (
          <img 
            src={player.photo} 
            className={`${size} rounded-full object-cover border-2 border-gray-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            alt={player.player || player.name}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />
        )}
        
        {/* Neutral avatar fallback */}
        <div 
          className={`${size} rounded-full flex items-center justify-center border-2 border-gray-200 bg-gray-400 relative ${(!player.photo || imageError || !imageLoaded) ? 'block' : 'hidden'}`}
        >
          {/* Neutral avatar SVG */}
          <svg 
            className={`${size === 'w-12 h-12' ? 'w-8 h-8' : 'w-6 h-6'} text-white`}
            fill="currentColor" 
            viewBox="0 0 24 24"
          >
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          
          {/* Overlay badge */}
          <div 
            className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold text-white border border-white"
            style={{
              backgroundColor: showStats 
                ? '#7C3AED' 
                : `#${player.position === "G" ? teamColors?.goalPrimary || '4B5563' : teamColors?.primary || '7C3AED'}`
            }}
          >
            {showStats ? statsValue : (player.number || '?')}
          </div>
          
          {player.position === "G" && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border border-white" />
          )}
        </div>
      </div>
    );
  };

  // PropTypes for PlayerImage
  PlayerImage.propTypes = {
    player: PropTypes.shape({
      photo: PropTypes.string,
      player: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      position: PropTypes.string
    }).isRequired,
    size: PropTypes.string,
    showStats: PropTypes.bool,
    statsValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    teamColors: PropTypes.shape({
      primary: PropTypes.string,
      goalPrimary: PropTypes.string,
      goalBorder: PropTypes.string,
      border: PropTypes.string,
      number: PropTypes.string,
      goalNumber: PropTypes.string
    })
  };

  // Format date and time
  const formatMatchDateTime = (dateString) => {
    if (!dateString) return { date: '', time: '' };
    
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    };
  };

  const { date: matchDate, time: matchTime } = formatMatchDateTime(matchData?.date);

  const predictions = [
    { title: matchData?.resultPrediction, subtitle: "1X2/Double Chance", value: `${(matchData?.resultProba * 100).toFixed(2)}%`, icon: Trophy },
    { title: matchData?.ggngPrediction === "0" ? "NG" : matchData?.ggngPrediction === "1" ? "GG" : "NED", subtitle: "GG/NG", value: `${(matchData?.ggngProba * 100).toFixed(2)}%`, icon: Target },
    { title: matchData?.overUnderPrediction, subtitle: "Over/Under", value: `${(matchData?.overUnderProba * 100).toFixed(2)}%`, icon: Goal }
  ];

  // Calculate top players
  const computeTopTargetPlayers = useCallback(() => {
    if (!matchData) return [];

    const enrichPlayers = (list, teamName) =>
      (list || []).map((p) => ({
        ...p,
        teamName,
      }));

    const allPlayers = [
      ...enrichPlayers(matchData?.homeStartingLineUp, matchData?.homeTeam),
      ...enrichPlayers(matchData?.awayStartingLineUp, matchData?.awayTeam),
    ];

    const processed = allPlayers.map((p) => {
      const shots = p.shotsOnTarget ?? p.shots ?? 0;
      const appearances = p.appearances ?? p.apps ?? p.played ?? 1;
      const avgShots = appearances ? Math.round(shots / appearances) : 0;

      const teamName = p.teamName;
      const teamEmblem = teamName === matchData?.homeTeam ? matchData?.homeTeamEmblem : matchData?.awayTeamEmblem;

      return {
        name: p.player,
        role: p.position,
        team: teamName,
        score: avgShots,
        photo: p.photo,
        teamEmblem,
      };
    });

    return processed
      .filter((p) => p.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [matchData]);

  const topPlayers = computeTopTargetPlayers();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading match details...</p>
        </div>
      </div>
    );
  }

  const Header = () => (
    <div 
      className="w-full text-white relative overflow-hidden"
      style={{ 
        backgroundImage: 'url(../../assets/images/header.png)', 
        backgroundSize: 'cover', 
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Optional overlay for better text readability - you can remove this if not needed */}
      <div className="bg-[#2C0053]">
        <div className="flex items-center justify-between p-4">
          <ArrowLeft 
            size={24} 
            className="cursor-pointer hover:opacity-80 transition-opacity text-white drop-shadow-lg" 
            onClick={() => window.history.back()} 
          />
        </div>
        <div className="text-center p-6">
          <div className="flex items-center justify-between max-w-md mx-auto px-8 relative bottom-8">
            <img src={matchData?.homeTeamEmblem} className="w-24 h-24 object-contain drop-shadow-lg" alt="Home team" />
            <div className="text-center">
              <p className="text-sm font-light text-white drop-shadow-md">{matchDate}</p>
              <p className="text-xl font-bold text-white drop-shadow-md">{matchTime}</p>
            </div>
            <img src={matchData?.awayTeamEmblem} className="w-24 h-24 object-contain drop-shadow-lg" alt="Away team" />
          </div>
          <div className="flex justify-between max-w-sm mx-auto mt-2 relative bottom-7">
            <p className="text-base text-white drop-shadow-md font-medium">{matchData?.homeTeam}</p>
            <p className="text-base text-white drop-shadow-md font-medium">{matchData?.awayTeam}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const Tabs = () => (
    <div className="bg-white border-b border-gray-100">
      <div className="flex justify-around mt-4">
        {["predictions", "lineups", "stats"].map((tab) => (
          <button 
            key={tab} 
            onClick={() => setActiveTab(tab)} 
            className="flex-1 pb-3 text-center transition-all duration-300"
          >
            <span className={`${
              activeTab === tab 
                ? "text-purple-600 font-semibold scale-110" 
                : "text-gray-400 font-normal"
            } transition-all duration-300 inline-block`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </span>
          </button>
        ))}
      </div>
      <div className="flex w-full h-1">
        {["predictions", "lineups", "stats"].map((tab) => (
          <div
            key={tab}
            className={`flex-1 transition-all duration-300 ${
              activeTab === tab ? "bg-purple-600 scale-x-100" : "bg-transparent scale-x-0"
            }`}
          />
        ))}
      </div>
    </div>
  );

  const PredictionsTab = () => (
    <div className="px-4 py-4">
      {predictions.map((item, index) => {
        const IconComponent = item.icon;
        return (
          <div 
            key={`prediction-${index}`} 
            className="flex items-center justify-between py-3 border-b border-gray-100 animate-in slide-in-from-left duration-500"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex items-center">
              <div className="animate-pulse mr-3">
                <IconComponent size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-purple-800 text-base font-semibold">{item.title}</p>
                <p className="text-gray-400 text-xs">{item.subtitle}</p>
              </div>
            </div>
            {item.value ? (
              <div className="bg-gray-100 px-3 py-1 rounded-lg font-medium animate-pulse">
                {item.value}
              </div>
            ) : (
              <button 
                onClick={() => setShowTeamModal(true)} 
                className="border border-gray-200 py-3 rounded-xl px-3 flex items-center hover:bg-gray-50 transition-colors"
              >
                <span className="text-sm mr-2">{selectedTeam}</span>
                <ChevronDown size={16} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  const FootballPitch = () => {
    const selectedTeamData = selectedLineupTeam === 'home' ? {
      name: matchData?.homeTeam,
      emblem: matchData?.homeTeamEmblem,
      formation: matchData?.homeFormation,
      coach: matchData?.homeCoach,
      colors: matchData?.homeColors,
      startingLineup: matchData?.homeStartingLineUp || [],
      substitutes: matchData?.homeSubstitutes || []
    } : {
      name: matchData?.awayTeam,
      emblem: matchData?.awayTeamEmblem,
      formation: matchData?.awayFormation,
      coach: matchData?.awayCoach,
      colors: matchData?.awayColors,
      startingLineup: matchData?.awayStartingLineUp || [],
      substitutes: matchData?.awaySubstitutes || []
    };

    // ONLY use actual gridX and gridY data to create formation - NO OTHER METHOD
    const getFormationLayout = () => {
      const players = selectedTeamData.startingLineup;
      
      if (!players || players.length === 0) return [];
      
      // Group players by their actual gridX (formation lines)
      const lineGroups = {};
      
      players.forEach(player => {
        const lineKey = player.gridX || 1; // Default to 1 if no gridX
        if (!lineGroups[lineKey]) {
          lineGroups[lineKey] = [];
        }
        lineGroups[lineKey].push(player);
      });

      // Sort each line by gridY (left to right positioning)
      Object.keys(lineGroups).forEach(lineKey => {
        lineGroups[lineKey].sort((a, b) => (a.gridY || 0) - (b.gridY || 0));
      });

      // Convert to array and sort by gridX (back to front: GK=1, DEF=2, MID=3, ATT=4,5...)
      const sortedLines = Object.keys(lineGroups)
        .map(key => ({
          gridX: parseInt(key),
          players: lineGroups[key]
        }))
        .sort((a, b) => b.gridX - a.gridX); // Reverse order: highest gridX first (forwards)

      return sortedLines;
    };

    const formationLayout = getFormationLayout();

    const PlayerCard = ({ player, isGK = false }) => {
      return (
        <div className="flex flex-col items-center">
          <div className="mb-1">
            <PlayerImage 
              player={player} 
              size={isGK ? 'w-12 h-12' : 'w-10 h-10'}
              teamColors={selectedTeamData.colors}
            />
          </div>
          
          <div className="bg-black bg-opacity-75 px-2 py-0.5 rounded text-white text-xs font-medium text-center min-w-0 max-w-20">
            <span className="block truncate">
              {player.player && player.player.includes(' ') ? player.player.split(' ').pop() : player.player}
            </span>
          </div>
        </div>
      );
    };

    // PropTypes for PlayerCard
    PlayerCard.propTypes = {
      player: PropTypes.shape({
        player: PropTypes.string.isRequired,
        number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        position: PropTypes.string.isRequired,
        photo: PropTypes.string,
        gridX: PropTypes.number,
        gridY: PropTypes.number
      }).isRequired,
      isGK: PropTypes.bool
    };

    const FormationLine = ({ line, lineIndex }) => {
      const playerCount = line.players.length;
      const isGoalkeeperLine = line.gridX === 1;
      
      // FIXED spacing - NO responsive variations
      const getGridCols = () => {
        switch(playerCount) {
          case 1: return 'grid-cols-1';
          case 2: return 'grid-cols-2';
          case 3: return 'grid-cols-3';
          case 4: return 'grid-cols-4';
          case 5: return 'grid-cols-5';
          default: return 'grid-cols-5';
        }
      };

      const getGap = () => {
        switch(playerCount) {
          case 1: case 2: return 'gap-8';
          case 3: return 'gap-6';
          case 4: return 'gap-4';
          case 5: default: return 'gap-3';
        }
      };

      // For goalkeeper, center single player
      if (isGoalkeeperLine) {
        return (
          <div 
            className="flex justify-center animate-in slide-in-from-bottom duration-500"
            style={{ animationDelay: `${lineIndex * 200}ms` }}
          >
            <PlayerCard 
              player={line.players[0]} 
              isGK={true}
            />
          </div>
        );
      }

      return (
        <div 
          className={`grid ${getGridCols()} ${getGap()} justify-items-center w-full max-w-sm mx-auto animate-in slide-in-from-bottom duration-500`}
          style={{ animationDelay: `${lineIndex * 200}ms` }}
        >
          {line.players.map((player, index) => (
            <PlayerCard 
              key={`line-${line.gridX}-player-${player.gridY || index}`} 
              player={player} 
            />
          ))}
        </div>
      );
    };

    // PropTypes for FormationLine
    FormationLine.propTypes = {
      line: PropTypes.shape({
        gridX: PropTypes.number.isRequired,
        players: PropTypes.arrayOf(
          PropTypes.shape({
            player: PropTypes.string.isRequired,
            number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            position: PropTypes.string.isRequired,
            photo: PropTypes.string,
            gridX: PropTypes.number,
            gridY: PropTypes.number
          })
        ).isRequired
      }).isRequired,
      lineIndex: PropTypes.number.isRequired
    };

    // Early return if no formation data
    if (!formationLayout || formationLayout.length === 0) {
      return (
        <div className="rounded-xl mx-2 mb-6 relative overflow-hidden shadow-lg bg-gradient-to-b from-green-400 via-green-500 to-green-600 p-4">
          <div className="text-center py-12">
            <p className="text-white">Formation data loading...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-xl mx-2 mb-6 relative overflow-hidden shadow-lg bg-gradient-to-b from-green-400 via-green-500 to-green-600 p-4">
        {/* Pitch background pattern */}
        <div className="absolute inset-0 opacity-30">
          {Array.from({ length: 10 }).map((_, i) => (
            <div 
              key={`stripe-${i}`}
              className="absolute w-full h-8"
              style={{ 
                top: i * 40, 
                backgroundColor: i % 2 ? "#16a34a" : "#15803d" 
              }} 
            />
          ))}
        </div>

        {/* Team header */}
        <div className="relative z-10 flex items-center justify-center mb-6">
          <div className="flex items-center bg-black bg-opacity-80 px-4 py-2 rounded-full">
            <img src={selectedTeamData.emblem} className="w-4 h-4 sm:w-6 sm:h-6 mr-3" alt="Team emblem" />
            <div className="text-white text-sm">
              <span className="font-bold">{selectedTeamData.name}</span>
              <span className="mx-2">•</span>
              <span className="text-gray-300">{selectedTeamData.formation}</span>
            </div>
          </div>
        </div>

        {/* Formation layout using ONLY gridX/gridY data */}
        <div className="relative z-10 space-y-6">
          {formationLayout.map((line, index) => (
            <FormationLine 
              key={`formation-line-${line.gridX}`}
              line={line}
              lineIndex={index}
            />
          ))}
        </div>

        {/* Subtle pitch markings */}
        <div className="absolute inset-0 pointer-events-none opacity-20">
          <div className="absolute w-full border-t border-white top-1/2" />
          <div className="absolute border border-white rounded-full w-16 h-16 top-1/2 left-1/2 transform -translate-x-8 -translate-y-8" />
          <div className="absolute w-1 h-1 bg-white rounded-full top-1/2 left-1/2 transform -translate-x-0.5 -translate-y-0.5" />
        </div>
      </div>
    );
  };

  const LineupsTab = () => {
    const selectedTeamData = selectedLineupTeam === 'home' ? {
      name: matchData?.homeTeam,
      emblem: matchData?.homeTeamEmblem,
      formation: matchData?.homeFormation,
      coach: matchData?.homeCoach,
      colors: matchData?.homeColors,
      startingLineup: matchData?.homeStartingLineUp || [],
      substitutes: matchData?.homeSubstitutes || []
    } : {
      name: matchData?.awayTeam,
      emblem: matchData?.awayTeamEmblem,
      formation: matchData?.awayFormation,
      coach: matchData?.awayCoach,
      colors: matchData?.awayColors,
      startingLineup: matchData?.awayStartingLineUp || [],
      substitutes: matchData?.awaySubstitutes || []
    };

    // Ensure we have valid data before rendering
    if (!selectedTeamData.name || !selectedTeamData.startingLineup) {
      return (
        <div className="px-4 py-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading lineup...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="px-4 py-4">
        <div className="flex mb-6 space-x-4">
          {[
            { key: 'home', name: matchData?.homeTeam, emblem: matchData?.homeTeamEmblem },
            { key: 'away', name: matchData?.awayTeam, emblem: matchData?.awayTeamEmblem }
          ].map((team) => (
            <button
              key={`team-select-${team.key}`}
              onClick={() => setSelectedLineupTeam(team.key)}
              className={`flex-1 py-3 px-4 rounded-full flex items-center justify-center transition-all duration-300 ${
                selectedLineupTeam === team.key ? "bg-purple-600 text-white scale-105" : "bg-gray-100 text-gray-600"
              }`}
            >
              <img
                src={team.emblem}
                className="w-5 h-5 mr-2"
                alt={team.name}
              />
              <span className="font-medium">{team.name}</span>
            </button>
          ))}
        </div>

        {selectedTeamData.startingLineup.length > 0 ? (
          <>
            <FootballPitch />

            {[
              { label: "Formation", value: selectedTeamData.formation },
              { label: "Coach", value: selectedTeamData.coach }
            ].map((item, i) => (
              <div 
                key={`team-info-${i}`} 
                className="flex items-center mb-4 mt-4 animate-in slide-in-from-left duration-500"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <Target size={28} className="text-purple-600 mr-3" />
                <div>
                  <p className="font-semibold text-base text-purple-800">{item.value}</p>
                  <p className="text-gray-500 text-xs">{item.label}</p>
                </div>
              </div>
            ))}

            {["Starting Lineup", "Substitutes"].map((section, sectionIndex) => (
              <div key={`section-${sectionIndex}`}>
                <h3 
                  className={`text-lg font-semibold mb-4 ${sectionIndex ? "mt-6" : ""} animate-in slide-in-from-left duration-500`}
                  style={{ animationDelay: `${sectionIndex * 100 + 200}ms` }}
                >
                  {section}
                </h3>
                {(section === "Starting Lineup" ? selectedTeamData.startingLineup : selectedTeamData.substitutes).map(
                  (player, index) => (
                    <div
                      key={`player-${sectionIndex}-${index}`}
                      className="flex items-center py-3 border-b border-gray-100 animate-in fade-in-50 slide-in-from-bottom-2 duration-500"
                      style={{ animationDelay: `${index * 50 + sectionIndex * 100}ms` }}
                    >
                      <div className="mr-3">
                        <PlayerImage 
                          player={player} 
                          teamColors={selectedTeamData.colors}
                        />
                      </div>

                      <div className="flex-1">
                        <p className="font-semibold text-sm">{player.player}</p>
                        <p className="text-gray-400 text-xs">
                          {player.position === "D" ? "Defender" : 
                           player.position === "M" ? "Midfielder" : 
                           player.position === "F" ? "Forward" : 
                           player.position === "G" ? "Goalkeeper" : player.position}
                        </p>
                      </div>
                      <span className="text-gray-600 font-medium">{player.number}</span>
                    </div>
                  )
                )}
              </div>
            ))}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">
              Lineup data not available for {selectedTeamData.name}
            </p>
          </div>
        )}
      </div>
    );
  };

  const StatsTab = () => {
    const calculateFormPercentage = (formString) => {
      if (!formString) return 0;
      const percentage = parseFloat(formString);
      return isNaN(percentage) ? 0 : percentage;
    };

    const getH2HResults = () => {
      if (!matchData?.h2h || !matchData.h2hFetched) return [];
      return matchData.h2h.slice(-5);
    };

    const h2hResults = getH2HResults();

    return (
      <div className="px-4 py-6">
        <div className="flex justify-center items-center mb-8">
          {[matchData?.homeTeam, matchData?.awayTeam].map((team, i) => (
            <div key={`team-stat-${i}`} className={`flex items-center ${i ? "ml-8" : "mr-8"}`}>
              <div className={`w-4 h-4 ${i ? "bg-purple-600" : "bg-green-400"} rounded mr-2`} />
              <span className="font-medium text-sm">{team}</span>
            </div>
          ))}
        </div>

        {matchData?.h2hFetched && h2hResults.length > 0 && (
          <div className="text-center mb-8">
            <div className="bg-black px-6 py-3 rounded-full mb-6 inline-block">
              <span className="text-white font-semibold text-sm">Last 5 Games (H2H)</span>
            </div>

            <div className="flex justify-between items-center w-full">
              {/* Home team H2H results */}
              <div className="text-center flex-1">
                <div className="mb-2 space-y-1">
                  {h2hResults.map((match, j) => {
                    const homeWon = match.home > match.away;
                    const awayWon = match.away > match.home;
                    const result = homeWon ? 'W' : awayWon ? 'L' : 'D';
                    
                    return (
                      <div
                        key={`h2h-home-${j}`}
                        className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold text-xs mx-auto mb-1 animate-in zoom-in duration-500 ${
                          result === 'W' ? 'bg-gray-800' : result === 'D' ? 'bg-gray-500' : 'bg-gray-400'
                        }`}
                        style={{ animationDelay: `${j * 100}ms` }}
                      >
                        {result}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Form progress */}
              <div className="text-center flex-1">
                {[
                  { team: matchData?.homeTeam, form: matchData?.teamForm?.home, color: "green-400" },
                  { team: matchData?.awayTeam, form: matchData?.teamForm?.away, color: "purple-600" }
                ].map((teamData, i) => {
                  const formPercentage = calculateFormPercentage(teamData.form);
                  
                  return (
                    <div
                      key={`form-${i}`}
                      className="mb-4 animate-in spin-in duration-1000"
                      style={{ animationDelay: `${i * 100 + 300}ms` }}
                    >
                      <div className="w-24 h-24 mx-auto rounded-full bg-gray-100 flex items-center justify-center relative">
                        <div className="absolute w-24 h-24 rounded-full border-4 border-gray-200" />
                        <div 
                          className={`absolute w-24 h-24 rounded-full border-4 border-t-transparent transition-all duration-1000 ${
                            teamData.color === "green-400" ? "border-green-400" : "border-purple-600"
                          }`}
                          style={{ 
                            transform: `rotate(${(formPercentage / 100) * 360}deg)` 
                          }}
                        />
                        <div className="text-center z-10">
                          <p className="font-bold text-xl text-gray-800">{formPercentage}%</p>
                          <p className="font-normal text-xs text-gray-500">FORM</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Away team H2H results */}
              <div className="text-center flex-1">
                <div className="mb-2 space-y-1">
                  {h2hResults.map((match, j) => {
                    const homeWon = match.home > match.away;
                    const awayWon = match.away > match.home;
                    const result = awayWon ? 'W' : homeWon ? 'L' : 'D';
                    return (
                      <div
                        key={`h2h-away-${j}`}
                        className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold text-xs mx-auto mb-1 animate-in zoom-in duration-500 ${
                          result === 'W' ? 'bg-gray-800' : result === 'D' ? 'bg-gray-500' : 'bg-gray-400'
                        }`}
                        style={{ animationDelay: `${j * 100 + 100}ms` }}
                      >
                        {result}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Comparison Stats */}
        {matchData?.teamComparisonFetched && (
          <>
            {[
              { 
                label: 'STRENGTH', 
                home: matchData?.teamStrength?.home, 
                away: matchData?.teamStrength?.away 
              },
              { 
                label: 'ATTACKING POTENTIAL', 
                home: matchData?.teamAttack?.home, 
                away: matchData?.teamAttack?.away 
              },
              { 
                label: 'DEFENSIVE POTENTIAL', 
                home: matchData?.teamDefense?.home, 
                away: matchData?.teamDefense?.away 
              }
            ].map((stat, index) => {
              const homePercentage = parseFloat(stat.home || '0');
              const awayPercentage = parseFloat(stat.away || '0');
              const total = homePercentage + awayPercentage;
              const homeWidth = total > 0 ? (homePercentage / total) * 100 : 50;
              const awayWidth = total > 0 ? (awayPercentage / total) * 100 : 50;

              return (
                <div
                  key={`stat-${index}`}
                  className="mb-8 animate-in slide-in-from-left duration-500"
                  style={{ animationDelay: `${index * 100 + 200}ms` }}
                >
                  <p className="text-center font-semibold text-sm mb-4 text-gray-800">{stat.label}</p>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-base">{homePercentage}%</span>
                    <span className="font-bold text-base">{awayPercentage}%</span>
                  </div>
                  <div className="flex h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="bg-green-400 h-full transition-all duration-1000 ease-out"
                      style={{ 
                        width: `${homeWidth}%`,
                        animationDelay: `${index * 100 + 300}ms`
                      }}
                    />
                    <div 
                      className="bg-purple-600 h-full transition-all duration-1000 ease-out"
                      style={{ 
                        width: `${awayWidth}%`,
                        animationDelay: `${index * 100 + 400}ms`
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Fallback message if no data available */}
        {!matchData?.h2hFetched && !matchData?.teamComparisonFetched && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              Statistics data not available for this match
            </p>
          </div>
        )}
      </div>
    );
  };

  const PlayerStats = () => (
    <div className="bg-white px-4 py-4">
      <h3 className="text-lg font-normal mb-4 animate-in fade-in duration-500">
        Expected Shots on Target
      </h3>
      {topPlayers.map((player, index) => (
        <div
          key={`player-stat-${index}`}
          className="flex items-center justify-between py-3 border-b border-gray-100 animate-in slide-in-from-left duration-500"
          style={{ animationDelay: `${index * 100 + 300}ms` }}
        >
          <div className="flex items-center">
            <div className="mr-3">
              <PlayerImage 
                player={{
                  photo: player.photo,
                  name: player.name,
                  player: player.name,
                  number: player.score,
                  position: player.role
                }} 
                showStats={true}
                statsValue={player.score}
              />
            </div>

            <div>
              <p className="font-semibold text-sm">{player.name}</p>
              <p className="text-gray-400 text-xs">
                {player.role === "D" ? "Defender" : 
                 player.role === "M" ? "Midfielder" : 
                 player.role === "F" ? "Forward" : 
                 player.role === "G" ? "Goalkeeper" : player.role}
              </p>
            </div>
            {player.teamEmblem && (
              <img
                src={player.teamEmblem}
                className="w-5 h-5 ml-2 object-contain"
                alt="Team emblem"
              />
            )}
          </div>
          <span 
            className="text-purple-800 font-semibold text-base animate-in zoom-in duration-500"
            style={{ animationDelay: `${index * 100 + 400}ms` }}
          >
            {player.score}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-version={COMPONENT_VERSION}>
      {/* Cache busting comment: Updated formation layout v2.1.0 */}
      <Header />
      <Tabs />
      
      <div className="max-h-screen overflow-y-auto">
        {activeTab === "predictions" && <PredictionsTab />}
        {activeTab === "lineups" && <LineupsTab />}
        {activeTab === "stats" && <StatsTab />}
        {activeTab === "predictions" && <PlayerStats />}
        <div className="h-10" />
      </div>

      {/* Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-end items-end z-50">
          <div 
            className="fixed inset-0" 
            onClick={() => setShowTeamModal(false)}
          />
          <div 
            className="bg-white p-4 rounded-t-2xl w-full max-w-md animate-in slide-in-from-bottom duration-300"
            style={{ zIndex: 51 }}
          >
            {(matchData ? [matchData.homeTeam, matchData.awayTeam] : []).map((team) => (
              <button
                key={`modal-team-${team}`}
                className="w-full py-6 border-b border-gray-100 text-center text-base hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setSelectedTeam(team);
                  setShowTeamModal(false);
                }}
              >
                <span className={`transition-transform duration-200 inline-block ${
                  selectedTeam === team ? "scale-110 font-semibold" : ""
                }`}>
                  {team}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Development helper - only shows on localhost */}
      {window.location.hostname === 'localhost' && (
        <div className="fixed bottom-4 left-4 bg-black text-white px-2 py-1 rounded text-xs z-50">
          {COMPONENT_VERSION}
        </div>
      )}
    </div>
  );
}

// PropTypes for the main component
IndividualMatchDetails.propTypes = {
  // This component doesn't receive any props directly as it uses URL params and API calls
};

IndividualMatchDetails.defaultProps = {
  // No default props needed for this component
};